import React from 'react';
import { motion } from 'framer-motion';
import Section from '../../components/ui/Section';

const Evolution = () => {
  const phases = [
    {
      num: 'Phase I',
      title: 'Identitäts-Shift & Wohlstands-Bewusstsein',
      content: (
        <>
          <p className="mb-4">
            Wir beginnen am absoluten Kern: deinem energetischen Grundniveau. Die meisten
            Frauen blockieren unbewusst genau den Wohlstand und Erfolg, den sie bewusst
            anstreben – weil ihr Nervensystem massive Sichtbarkeit und Geld mit Gefahr,
            Ablehnung oder überwältigender Verantwortung assoziiert. Wir werden die
            Ahnenketten harter Arbeit und die tief verwurzelte Überzeugung auflösen,
            dass „brave Mädchen klein bleiben".
          </p>
          <p className="mb-4">
            In dieser Phase initiieren wir einen tiefgreifenden{' '}
            <strong>Quantum Leap</strong> in deiner Identität. Wir lösen deine
            Geldblockaden auf und erweitern deine Fähigkeit, massiven Wohlstand ohne
            Schuldgefühle zu halten. Du wirst dir die unentschuldigte Erlaubnis geben,
            zu strahlen. Du hörst auf, dich als „Hartarbeitende" zu identifizieren,
            und beginnst, die souveräne Schöpferin zu verkörpern.
          </p>
          <p>
            Wenn deine Selbstwert-Architektur neu kalibriert ist, hat deine äußere
            Realität keine andere Wahl als sich zu verändern. Du wirst buchstäblich
            spüren, wie sich das physische Gewicht von deiner Brust hebt, wenn du
            erkennst, dass deine Ausstrahlung allein ausreicht, um hochwertige
            Möglichkeiten anzuziehen.
          </p>
        </>
      ),
    },
    {
      num: 'Phase II',
      title: 'Strategisches Magnetisches Marketing',
      content: (
        <>
          <p className="mb-4">
            Marketing im neuen Paradigma geht nicht darum, zu überzeugen, zu manipulieren
            oder zu performen. Es ist eine Übertragung deiner tiefsten Wahrheit. In Phase II
            befreien wir uns von den erzwungenen, maskulinen Verkaufstrichtern, die sich
            nicht im Einklang mit deiner Seele anfühlen. Stattdessen bauen wir eine{' '}
            <strong>Soul-Business-Architektur</strong>, die auf magnetischer Resonanz basiert.
          </p>
          <p className="mb-4">
            Du lernst, deinen Wert mit unentschuldigter Klarheit zu artikulieren. Wir
            verfeinern deine Botschaft so, dass sie wie ein Leuchtturm wirkt: Sie stößt
            sofort diejenigen ab, die deine Energie erschöpfen, und zieht unwiderstehlich
            Premium-Klientinnen an, die bereit, willens und begeistert sind, in deine
            hochwertigsten Angebote zu investieren.
          </p>
          <p>
            Sichtbarkeit wird keine Angst mehr auslösen. Du wirst mit souveräner Präsenz
            auf die digitale Bühne treten. Du musst nicht 10 Mal am Tag posten oder
            erschöpfenden Trends folgen. Du sprichst aus deiner Queen-Energie heraus –
            und wenn die Queen spricht, hört das richtige Publikum zu, fühlt sich tief
            verstanden und konvertiert mühelos.
          </p>
        </>
      ),
    },
    {
      num: 'Phase III',
      title: 'Skalierbare Souveränität',
      content: (
        <>
          <p className="mb-4">
            Ein wahres Imperium dient deiner Freiheit – es verzehrt nicht dein Leben.
            Die letzte Phase dreht sich darum, deine neue Frequenz in den physischen
            Strukturen deines Unternehmens zu verankern. Wir bringen dich aus dem
            Tagesgeschäft heraus und in den Visionärs-Sitz hinein.
          </p>
          <p className="mb-4">
            Wir werden intuitive Systeme und hochwertige Liefermodelle entwerfen, die es
            dir ermöglichen, deine Wirkung zu skalieren und dabei deine Arbeitsstunden
            radikal zu reduzieren. Du lernst, dein Team, deine Klientinnen und deine
            Community vollständig aus deiner weiblichen Intuition heraus zu führen –
            Entscheidungen aus einem Ort des tiefen Vertrauens statt aus angstbasierter
            Logik zu treffen.
          </p>
          <p>
            Hier verwandelt sich dein Business in ein <strong>Vermächtnis</strong>. Du
            wirst die ultimative Befreiung erleben: ein hochprofitables, skalierbares
            Unternehmen zu führen, das sich wie eine natürliche Erweiterung des Zwecks
            deiner Seele anfühlt. Du wirst unberührbar, tief entspannt und unendlich
            mächtig.
          </p>
        </>
      ),
    },
  ];

  return (
    <Section className="bg-dark relative border-t border-white/5">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-24">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.2em] uppercase text-sm mb-4"
          >
            Die 6-Monats-Evolution
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-serif text-cream"
          >
            Feminine Business{' '}
            <span className="italic text-gradient-gold">Intelligenz</span>
          </motion.h2>
        </div>

        <div className="space-y-32">
          {phases.map((phase, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="flex flex-col md:flex-row gap-8 md:gap-16">
                <div className="md:w-1/3 shrink-0">
                  <div className="sticky top-32">
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">
                      {phase.num}
                    </span>
                    <h3 className="text-3xl font-serif text-cream mt-2 leading-tight">
                      {phase.title}
                    </h3>
                    <div className="w-12 h-1 bg-gradient-to-r from-gold to-transparent mt-6" />
                  </div>
                </div>
                <div className="md:w-2/3 text-cream/70 font-light text-lg leading-relaxed">
                  {phase.content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Evolution;